/**
 * Quick Link Form Interface
 * Strongly-typed reactive form structure for creating/editing quick links
 */

import { FormControl } from "@angular/forms";
import {
  QuickLinkActionType,
  RedirectConfig,
  RedirectType,
  UsageLimitType,
} from "./quick-link-list-item.interface";

export interface IQuickLinkForm {
  name: FormControl<string>;
  slug: FormControl<string>;
  actionType: FormControl<QuickLinkActionType>;
  usageLimitType: FormControl<UsageLimitType>;
  maxUses: FormControl<number | null>;
  requiresLogin: FormControl<boolean>;
  requiresConfirmation: FormControl<boolean>;
  expiredUtc: FormControl<Date | null>; // Changed from expiresAt to match API field name
  redirectType: FormControl<RedirectType>; // Numeric enum: 0=ThankYouPage, 1=V2Portal, 2=CustomUrl
  redirectMessage: FormControl<string | null>;
  redirectUrl: FormControl<string | null>;
  utmSource: FormControl<string | null>;
  utmMedium: FormControl<string | null>;
  utmCampaign: FormControl<string | null>;
  utmContent: FormControl<string | null>;
}

export interface QuickLinkFormData {
  name: string;
  slug: string;
  actionType: QuickLinkActionType;
  usageLimitType: UsageLimitType;
  maxUses: number | null;
  requiresLogin: boolean;
  expiresAt: Date | null;
  redirectConfig: RedirectConfig;
}

// Action type options for dropdowns
export interface ActionTypeOption {
  value: QuickLinkActionType;
  label: string;
  icon: string;
  description: string;
}

// ActionType enum: 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
export const ACTION_TYPE_OPTIONS: ActionTypeOption[] = [
  {
    value: 0,
    label: "Resume Subscription",
    icon: "pi pi-play",
    description: "Reactivate a paused subscription",
  },
  {
    value: 1,
    label: "Pause/Snooze Subscription",
    icon: "pi pi-pause",
    description: "Temporarily pause a subscription",
  },
  {
    value: 2,
    label: "Process Now",
    icon: "pi pi-forward",
    description: "Process the next scheduled order immediately",
  },
  {
    value: 3,
    label: "Reactivate Canceled Subscription",
    icon: "pi pi-refresh",
    description: "Restore a canceled subscription",
  },
];

// Redirect type options for dropdowns
export interface RedirectTypeOption {
  value: 0 | 1 | 2; // RedirectType numeric enum
  label: string;
  description: string;
}

// RedirectType enum: 0=ThankYouPage, 1=V2Portal, 2=CustomUrl
export const REDIRECT_TYPE_OPTIONS: RedirectTypeOption[] = [
  {
    value: 0,
    label: "Thank You Page",
    description: "Show a custom thank you message",
  },
  {
    value: 1,
    label: "Customer Portal (V2)",
    description: "Redirect to their subscription dashboard",
  },
  {
    value: 2,
    label: "Custom URL",
    description: "Redirect to a specific URL",
  },
];
